import {getDateTime, moment} from "@/src/modules/core/lib";
import {useMeStore} from "@/src/modules/core/store/me";

import {isDispatchShiftRole} from "../lib/emergency-shift-eligibility";

import {
  AdminActionSchema,
  BulkCopyRequest,
  BulkCopySchema,
  EmergencyShiftRequest,
  EmergencyShiftSchema,
  Shift,
  ShiftRequest,
  ShiftSchema,
  ShiftType,
  Template,
  TemplateRequest,
  TemplateSchema,
} from "../types";

class ShiftsSerializer {
  serializeEmergencyShift({
    type,
    properties,
    patrol_route,
    from_date,
    from_time,
    to_date,
    to_time,
    reason,
    gps_coordinates,
    gps_source,
  }: EmergencyShiftSchema): EmergencyShiftRequest {
    if (!type?.value) {
      throw new Error("Emergency shift type is required");
    }

    const me = useMeStore.getState().me;
    const omitProperties = me ? isDispatchShiftRole(me.role.name) : false;
    const isPatrol = type.value === ShiftType.PATROL;

    const payload: EmergencyShiftRequest = {
      type: type.value,
      from_datetime: getDateTime(from_date, from_time).utc().format(),
      to_datetime: getDateTime(to_date, to_time).utc().format(),
      reason,
      gps_coordinates,
      ...(gps_source ? {gps_source} : {}),
    };

    if (isPatrol) {
      if (patrol_route) {
        payload.patrol_route_id = patrol_route.id;
      }
    } else if (!omitProperties) {
      payload.properties = properties.map((p, i) => ({
        property_id: p.id,
        assignment_type: i === 0 ? "primary" : "secondary",
      }));
    }

    return payload;
  }

  serializeAdjustment(
    shiftId: number,
    {from_date, from_time, to_date, to_time, ...data}: AdminActionSchema,
  ) {
    return {
      ...data,
      shift_id: shiftId,
      from_datetime:
        from_date && from_time ? getDateTime(from_date, from_time).utc().format() : null,
      to_datetime: to_date && to_time ? getDateTime(to_date, to_time).utc().format() : null,
    };
  }

  /**
   * Drag-and-drop reschedule payload. Whitelist editable fields only — never
   * round-trip GET-only keys (`properties` for patrol, `patrol_route`,
   * `patrol_visits`, `user`, `worked_shift`, `company_vehicle`, etc.).
   * Patrol moves omit `properties` and `patrol_route_id` (no visit re-snapshot).
   */
  serializeShift(shift: Shift, user_id: number | null): ShiftRequest {
    const isPatrol = shift.type === ShiftType.PATROL;

    const base: ShiftRequest = {
      shift_id: shift.shift_id,
      type: shift.type as ShiftType,
      user_id,
      from_datetime: shift.from_datetime,
      to_datetime: shift.to_datetime,
      notes: shift.notes ?? "",
      notify_officer: false,
      breaks: shift.breaks.map((b) => ({
        shift_break_id: b.shift_break_id,
        break_type: b.break_type,
        start_datetime: b.start_datetime,
        is_paid: b.is_paid,
        duration_minutes: b.duration_minutes,
      })),
    };

    if (!isPatrol) {
      base.properties = shift.properties.map((p) => ({
        property_id: p.property.id,
        assignment_type: p.assignment_type,
        shift_property_id: p.shift_property_id,
      }));
    }

    return base;
  }

  serializeShiftFormData(
    {
      from_time,
      from_date,
      to_time,
      to_date,
      days,
      user,
      role_id,
      break_suggestion,
      patrol_route,
      ...values
    }: ShiftSchema,
    shift: Shift | null,
  ): ShiftRequest {
    const isPatrol = (values.type?.value ?? ShiftType.NORMAL) === ShiftType.PATROL;

    const shiftData: ShiftRequest = {
      ...values,
      type: values.type?.value ?? ShiftType.NORMAL,
      user_id: user!.first_name === "Unassigned" ? null : user!.user_id,
      from_datetime: getDateTime(from_date, from_time).utc().format(),
      to_datetime: getDateTime(to_date, to_time).utc().format(),
      properties: isPatrol
        ? []
        : values.properties.map((property, i) => {
            const propertyData = {
              property_id: property.id,
              assignment_type: i === 0 ? "primary" : "secondary",
            } as NonNullable<ShiftRequest["properties"]>[0];

            if (shift) {
              const shiftProperty = shift.properties.find((p) => p.property.id === property.id);
              if (shiftProperty) {
                propertyData.shift_property_id = shiftProperty.shift_property_id;
              }
            }

            return propertyData;
          }),
      breaks: values.breaks.map((b) => {
        const {start_date, start_time, is_paid, ...rest} = b;
        const breakItem = {
          is_paid: (is_paid ? 1 : 0) as 0 | 1,
          start_datetime: getDateTime(start_date, start_time).utc().format(),
          ...rest,
        };

        if (shift) {
          const shiftBreak = shift.breaks.find((sb) => sb.shift_break_id === b.shift_break_id);
          if (shiftBreak) {
            breakItem.shift_break_id = shiftBreak.shift_break_id;
          }
        }

        return breakItem;
      }),
    };

    if (isPatrol) {
      delete (shiftData as {properties?: unknown}).properties;
      const selectedRouteId = patrol_route?.id ?? null;
      const currentRouteId = shift?.patrol_route?.patrol_route_id ?? null;
      // Send only when creating or when the route actually changed (re-snapshots visits).
      if (selectedRouteId != null && (!shift || selectedRouteId !== currentRouteId)) {
        shiftData.patrol_route_id = selectedRouteId;
      }
    }

    if (days.length) {
      const shiftDay = getDateTime(from_date, from_time).day();
      shiftData.extended_dates = days.map(Number).map((day) => {
        const dif = day - shiftDay;
        return {
          from_datetime: getDateTime(from_date, from_time).add(dif, "days").utc().format(),
          to_datetime: getDateTime(to_date, to_time).add(dif, "days").utc().format(),
        };
      });
    }

    if (shift) {
      shiftData.shift_id = shift.shift_id;
    }

    return shiftData;
  }

  serializeTemplate(data: TemplateSchema, template: Template | null) {
    return {
      ...data,
      type: data.type?.value ?? ShiftType.NORMAL,
      from_time: `${data.from_time}:00`,
      to_time: `${data.to_time}:00`,
      properties: data.properties.map((property, index) => {
        const propertyData = {
          property_id: property.id,
          assignment_type: index === 0 ? "primary" : "secondary",
        } as TemplateRequest["properties"][0];

        if (template) {
          const {template_property_id} = template.properties.find(
            (p) => p.property.id === property.id,
          )!;

          propertyData.template_property_id = template_property_id;
        }

        return propertyData;
      }),
      breaks: data.breaks.map((b) => {
        const breakItem = {
          ...b,
          start_time: `${b.start_time}:00`,
        };

        if (template) {
          const templateBreak = template.breaks.find(
            (sb) => sb.template_break_id === b.template_break_id,
          );
          if (templateBreak) {
            breakItem.template_break_id = templateBreak.template_break_id;
          }
        }

        return breakItem;
      }),
    };
  }

  serializeTemplateToShift(template: Template) {
    const {from_time, to_time, properties, breaks} = template;
    let date = moment().format("YYYY-MM-DD");
    let fromDate = moment(`${date}T${from_time}`);

    if (fromDate.isBefore(moment())) {
      fromDate.add(1, "day");
      date = fromDate.format("YYYY-MM-DD");
    }

    let toDate = moment(`${date}T${to_time}`);

    if (toDate.isBefore(fromDate)) {
      toDate.add(1, "day");
    }

    return {
      user_id: null,
      from_datetime: fromDate.utc().format(),
      to_datetime: toDate.utc().format(),
      type: "normal",
      notify_officer: false,
      properties: properties.map((p) => {
        const {
          assignment_type,
          property: {id},
        } = p;

        return {
          assignment_type,
          property_id: id,
        };
      }),
      breaks: breaks.map((b) => {
        const {break_type, duration_minutes, is_paid, start_time} = b;

        let start_datetime = moment(`${date}T${start_time}`);
        if (start_datetime.isBefore(fromDate)) {
          start_datetime.add(1, "day");
        }

        return {
          break_type,
          duration_minutes,
          is_paid: is_paid ? 1 : 0,
          start_datetime: start_datetime.utc().format(),
        };
      }),
    } as ShiftRequest;
  }

  serializeBulkCopy(data: BulkCopySchema) {
    return {
      source_from: moment(data.source_from).format("YYYY-MM-DD"),
      source_to: moment(data.source_to).format("YYYY-MM-DD"),
      target_from: moment(data.target_from).format("YYYY-MM-DD"),
      target_to: moment(data.target_to).format("YYYY-MM-DD"),
    } as BulkCopyRequest;
  }
}

export const shiftsSerializer = new ShiftsSerializer();
